import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { useGlobalState } from '../../app/GlobalStateProvider';
import { getRoomOption, isRoomJoined } from '../../room';
import { toggleLoad } from '../loading';
import { getDisplayName } from '../settings';
import { appWillMount } from './actions';
import BaseApp from './components/BaseApp';
import { InitStyled } from './components/styles';
import { PAGE_TYPE } from './constants';
const BaseContainer = React.memo(() => {
    const dispatch = useDispatch();
    const { isDesktop } = useGlobalState();
    const roomUid = useSelector((state) => location.pathname.split('/').pop() || getRoomOption(state)?.uuid, shallowEqual);
    const { displayname, isJoined, appLoad, } = useSelector((state) => ({
        displayname: getDisplayName(state),
        isJoined: isRoomJoined(state),
        appLoad: !!state['features/base/app'].app,
    }), shallowEqual);
    const { enableLobby, enableNickname, individualLive, } = useSelector((state) => state['features/base/settings']);
    const [page, setPage] = useState(location.pathname.split('/')[1] === 'room'
        ? location.hash.substring(1)
        : location.pathname.split('/')[1] || PAGE_TYPE.LOBBY);
    // 오른쪽 클릭 방지 이벤트 핸들러
    const handlerPreventMenu = (e) => e.preventDefault();
    useEffect(() => {
        // 컴포넌트가 마운트될 때 contextmenu 이벤트 리스너 추가
        window.addEventListener('contextmenu', handlerPreventMenu);
        document.title = APP.config.title || 'Meetmate';
        return () => {
            dispatch(toggleLoad(false));
            dispatch(appWillMount(true));
            window.removeEventListener('contextmenu', handlerPreventMenu);
        };
    }, []);
    useEffect(() => {
        const quitUser = async () => await APP.API.quitMeeting();
        if (individualLive) {
            window.addEventListener('beforeunload', quitUser);
        }
        else {
            window.removeEventListener('beforeunload', quitUser);
        }
    }, [individualLive]);
    useEffect(() => {
        const handlerPage = (props) => {
            const { page } = props;
            if (appLoad) {
                if (page === PAGE_TYPE.LOBBY) {
                    if (!enableLobby)
                        setPage(PAGE_TYPE.ERROR);
                    else
                        setPage(PAGE_TYPE.LOBBY);
                }
                if (page === PAGE_TYPE.NICKNAME) {
                    if (!enableNickname)
                        setPage(PAGE_TYPE.DEVICE);
                    else
                        setPage(PAGE_TYPE.NICKNAME);
                    return;
                }
                setPage(page);
            }
        };
        if (appLoad) {
            APP.eventManager.subscribe('UPDATE_PAGE', handlerPage);
            document.querySelectorAll('img').forEach(img => {
                img.setAttribute('aria-hidden', 'true');
                img.style.pointerEvents = 'none';
            });
            const setVh = () => {
                const vh = window.innerHeight * 0.01;
                document.documentElement.style.setProperty('--vh', `${vh}px`);
            };
            setTimeout(setVh, 3000);
        }
        return () => {
            APP.eventManager.unsubscribe('UPDATE_PAGE', handlerPage);
        };
    }, [appLoad]);
    useEffect(() => {
        const setVh = () => {
            const vh = window.innerHeight * 0.01;
            const next = `${vh}px`;
            // 동일값 재설정 스킵 — visualViewport resize 는 키보드 애니메이션 중 다발하므로
            // innerHeight 가 실제로 변한 경우에만 스타일을 갱신한다(정상 기기에선 완전 no-op).
            if (document.documentElement.style.getPropertyValue('--vh') === next)
                return;
            document.documentElement.style.setProperty('--vh', next);
        };
        setVh();
        window.addEventListener('resize', setVh);
        window.addEventListener('orientationchange', setVh);
        // ★iOS 26.x 일부(실측: 아이폰 16 Pro Max / iOS 26.5.2)는 키보드 열림 시 window.innerHeight
        // 가 축소되어 resize 로 --vh 가 줄어드는데, 키보드 "닫힘" 시 window resize 가 간헐적으로
        // 발화하지 않아 --vh 가 축소값으로 고착된다 — 채팅을 닫아도 키보드+액세서리바가 있던
        // 영역만큼 화면이 줄어든 채 남는 실측 증상(26.5 기기는 정상, 마이너 버전 간 WebKit 차이).
        // visualViewport resize 는 키보드 닫힘(뷰포트 복원)에서 안정적으로 발화하므로 재계산
        // 트리거로 추가한다. setVh 는 innerHeight 기준 + 동일값 스킵이라, 키보드에 innerHeight 가
        // 변하지 않는 기기에서는 완전 no-op.
        // ★★반드시 smallMobile(폰) 한정 — iPad 는 키보드가 오버레이인데 전환 중 innerHeight 가
        // 일시적으로 흔들려, vv resize 로 --vh 를 재계산하면 그 흔들림을 latch 해 #root 가 실제로
        // 짧아진다(과거 4e34bb4b 가 정확히 이 방식으로 흰영역을 악화시켜 반증·금지됨 — 메모리
        // meetmate-viewport-vh 참조). 동일값 스킵으로는 "일시적 오보고 값의 latch"를 못 막는다.
        const smallMobile = screen.width <= 505 || screen.height <= 505;
        if (smallMobile)
            window.visualViewport?.addEventListener('resize', setVh);
        return () => {
            window.removeEventListener('resize', setVh);
            window.removeEventListener('orientationchange', setVh);
            if (smallMobile)
                window.visualViewport?.removeEventListener('resize', setVh);
        };
    }, []);
    // Method to handle page changes
    const handlerChangePage = (page) => {
        if (!appLoad)
            return;
        if (page === PAGE_TYPE.LOBBY) {
            if (!enableLobby)
                setPage(PAGE_TYPE.ERROR);
            else
                setPage(PAGE_TYPE.LOBBY);
        }
        else if (page === PAGE_TYPE.NICKNAME) {
            if (!enableNickname)
                setPage(PAGE_TYPE.DEVICE);
            else
                setPage(PAGE_TYPE.NICKNAME);
        }
        else {
            setPage(page);
        }
    };
    // Effect to handle initial load and room joining logic
    useEffect(() => {
        if (!appLoad) {
            dispatch(toggleLoad(true));
        }
        else {
            if (location.pathname.includes('room')) {
                if (isJoined) {
                    handlerChangePage(PAGE_TYPE.MAIN);
                }
                else {
                    if (roomUid) {
                        if (!displayname)
                            handlerChangePage(PAGE_TYPE.NICKNAME);
                        else
                            handlerChangePage(PAGE_TYPE.DEVICE);
                    }
                }
            }
        }
    }, [appLoad, isJoined, roomUid, displayname]);
    if (!appLoad)
        return null;
    return (React.createElement(BrowserRouter, null,
        React.createElement(InitStyled, { "$isDesktop": isDesktop }),
        React.createElement(BaseApp, { page: page, roomUid: roomUid })));
});
export default BaseContainer;
